import { useContext } from "react";
import { ThemeContext } from "styled-components";
import { Flex, Box, Text } from "rebass/styled-components";
import Trans from "next-translate/Trans";
import camelize from "camelize";
import { addYears, format } from "date-fns";

import ServiceIcon from "../common/ServiceIcon";
import Heading from "../common/Heading";
import Icon from "../common/Icon";
import Money from "../common/Money";
import { MetaContext } from "../meta";
import { Service } from "../../generated/graphql";

interface Props {
  services: Service[];
  id: string;
}

interface SizesProps {
  service: Service;
}

const copyFromSizes = (sizes: [string, boolean][]): string => {
  let copy = "accepts";
  //filter the truthy sizes and contactenates their label into copy
  sizes.filter(([_, val]) => val).map(([key]) => (copy += `_${key}`));

  return camelize(copy);
};

const DogSizes = ({ service }: SizesProps) => {
  const { small, medium, large, giant, puppy } = service.acceptsDogs;

  /* eslint-disable @typescript-eslint/camelcase */
  const sizes = Object.entries({
    dog_s: small,
    dog_m: medium,
    dog_l: large,
    dog_xl: giant
  });
  /* eslint-enable @typescript-eslint/camelcase */

  return (
    <>
      <Box pt={2} alignItems="flex-start">
        <Flex justifyContent="space-between" alignItems="center">
          <Text>
            <Trans i18nKey={`sitterprofile:${copyFromSizes(sizes)}`} />
          </Text>
          <Flex flexWrap="wrap" justifyContent="flex-end">
            <Flex alignItems="baseline" width={1} justifyContent="flex-end">
              {sizes.map(([size, isAvailable], key) => {
                return (
                  <Box key={key} pl={1}>
                    <Icon
                      name={size}
                      fontSize={2 + key}
                      width={"100%"}
                      color={isAvailable ? "springGreen" : "gray"}
                    />
                  </Box>
                );
              })}
            </Flex>
            {puppy && (
              <Flex pt={0} alignItems="flex-start" justifyContent="flex-end">
                <Box sx={{ basis: "100%" }} fontSize={3}>
                  <Icon name={"puppy"} color="springGreen" fontSize={1} />
                </Box>
                <Box sx={{ basis: "100%", marginLeft: "10px" }}>
                  <Text as="span" fontSize={1} mr={1}>
                    <Trans i18nKey={`sitterprofile:caregiverAcceptsPuppies`} />
                  </Text>
                </Box>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </>
  );
};

const Services = ({ services, id }: Props) => {
  const theme = useContext(ThemeContext);
  const { url } = useContext(MetaContext);

  return (
    <>
      <Box id={id} mt={4}>
        <Heading as="h2" fontSize={5}>
          <Trans i18nKey="sitterprofile:services" />
        </Heading>
        <Text fontSize={1} color="darkGray">
          <Trans i18nKey="sitterprofile:servicesDisclaimer" />
        </Text>
      </Box>
      {services.map(service => (
        <Box
          itemProp="offers"
          itemScope
          itemType="http://schema.org/Offer"
          key={service.serviceTypeId}
          mx={[-2, 0]}
          px={[2, 0]}
          sx={{
            paddingBottom: 3,
            borderBottom: `1px solid ${theme["colors"].gray}`
          }}
        >
          <meta
            itemProp="url"
            content={`${url}`}
            itemType="http://schema.org/Offer"
          />
          <meta
            itemProp="priceCurrency"
            content={`${service.currency}`}
            itemType="http://schema.org/Offer"
          />
          <meta
            itemProp="price"
            content={`${service.price}`}
            itemType="http://schema.org/Offer"
          />
          <meta
            itemProp="availability"
            content={`http://schema.org/InStock`}
            itemType="http://schema.org/Offer"
          />
          <meta
            itemProp="priceValidUntil"
            content={format(addYears(new Date(), 1), "YYY-MM-dd")}
            itemType="http://schema.org/Offer"
          />
          <Flex alignItems="center" pt={3}>
            <Box width={[2 / 8, 1 / 8]} fontSize={7}>
              <ServiceIcon
                serviceTypeId={service.serviceTypeId}
                color="springGreen"
              />
            </Box>
            <Box width={[6 / 8, 6 / 8]}>
              <Heading as="h3" fontWeight="bold" fontSize={2}>
                <Trans
                  i18nKey={`sitterprofile:serviceType${service.serviceTypeId}`}
                />
              </Heading>
              <Text fontSize={1} color="darkGray">
                <Trans
                  i18nKey={`sitterprofile:serviceTypeAnnotation${service.serviceTypeId}`}
                />
              </Text>
            </Box>
            <Box width={1 / 8} fontSize={5} fontWeight="book" textAlign="right">
              <Money
                currency={service.currency}
                amount={service.price}
                stripZeros
              />
            </Box>
          </Flex>

          {service.meetAndGreet && (
            <Flex>
              <Box flex={1}>
                <Text>
                  <Trans i18nKey="sitterprofile:meetAndGreet" />
                </Text>

                <Text fontSize={1} color="darkGray">
                  <Trans i18nKey="sitterprofile:meetAndGreetDesc" />
                </Text>
              </Box>

              <Box width={1 / 6} textAlign="right">
                <Text as="span">+</Text>

                <Money
                  currency={service.currency}
                  amount={service.meetAndGreetPrice!}
                  stripZeros
                />
              </Box>
            </Flex>
          )}

          {service.pickUpDropOff && (
            <Flex pt={2}>
              <Box width={5 / 6}>
                <Text>
                  <Trans i18nKey="sitterprofile:pickUpDropOff" />
                </Text>
                <Text fontSize={1} color="darkGray">
                  <Trans
                    i18nKey="sitterprofile:pickUpDropOffDisclaimer"
                    values={{
                      distance: service.pickUpDropOffDistance
                    }}
                  />
                </Text>
              </Box>
              <Box width={1 / 6} textAlign="right">
                <Text as="span">+</Text>
                <Money
                  currency={service.currency}
                  amount={service.pickUpDropOffPrice}
                  stripZeros
                />
              </Box>
            </Flex>
          )}

          {!!service.holidayPrice && (
            <Flex pt={2}>
              <Box width={5 / 6}>
                <Text>
                  <Trans i18nKey="sitterprofile:holidayRate" />
                </Text>
              </Box>
              <Box width={1 / 6} textAlign="right">
                <Money
                  currency={service.currency}
                  amount={service.holidayPrice}
                  stripZeros
                />
              </Box>
            </Flex>
          )}

          {!!service.puppyPrice && service.acceptsDogs?.puppy && (
            <Flex pt={2}>
              <Box width={5 / 6}>
                <Text>
                  <Trans i18nKey="sitterprofile:puppyRate" />
                </Text>
              </Box>
              <Box width={1 / 6} textAlign="right">
                <Money
                  currency={service.currency}
                  amount={service.puppyPrice}
                  stripZeros
                />
              </Box>
            </Flex>
          )}

          {!!service.additionalDogPrice && (
            <Flex pt={2}>
              <Box width={5 / 6}>
                <Text>
                  <Trans i18nKey="sitterprofile:additionalDogRate" />
                </Text>
              </Box>
              <Box width={1 / 6} textAlign="right">
                <Money
                  currency={service.currency}
                  amount={service.additionalDogPrice}
                  stripZeros
                />
              </Box>
            </Flex>
          )}

          <DogSizes service={service} />
        </Box>
      ))}
    </>
  );
};

export default Services;
